/**
 * Agent Knowledge Page.
 *
 * Shows the org's source-fed agent knowledge layer and unresolved gaps.
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Archive, Copy, ExternalLink, Loader2, Plus, RotateCcw } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/hooks/useOrganization';
import { useInterviewerSetup } from '@/hooks/useInterviewerSetup';
import knowledgeGapsService from '@/lib/api/knowledgeGaps';
import onboardingService from '@/lib/api/onboarding';
import { InterviewerLinksContent } from '@/pages/platform_views/InterviewerSetupPage';
import {
  AgentKnowledgeGapsTab,
  AttachKnowledgeDialog,
  KnowledgeTab,
  OverviewTab,
  SessionsTab,
  SourcesTab,
} from '@/pages/platform_views/InterviewerDetailPage';

const VALID_TABS = ['overview', 'knowledge', 'gaps', 'interviews', 'sources'] as const;
type AgentKnowledgeTab = (typeof VALID_TABS)[number];

const TrainingPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.id || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const normalizedRawTab =
    rawTab === 'sessions' || rawTab === 'conversations' || rawTab === 'coaching'
      ? 'interviews'
      : rawTab;
  const tabFromUrl: AgentKnowledgeTab = (VALID_TABS as readonly string[]).includes(normalizedRawTab ?? '')
    ? (normalizedRawTab as AgentKnowledgeTab)
    : 'overview';
  const [activeTab, setActiveTab] = useState<AgentKnowledgeTab>(tabFromUrl);
  const [activeGapsView, setActiveGapsView] = useState<'active' | 'analysis' | 'history'>('active');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [generationPending, setGenerationPending] = useState(false);
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const {
    primaryInterviewer,
    isLoading: interviewersLoading,
    reingestInterviewer,
    isReingesting,
    copyLink,
  } = useInterviewerSetup(orgId);
  const interviewerId = primaryInterviewer?.id;

  const { data: gapReport } = useQuery({
    queryKey: ['knowledge-gaps-report', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const response = await knowledgeGapsService.getReport(orgId);
      return response.success && response.data ? response.data : null;
    },
    enabled: !!orgId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const activeGapCount = gapReport?.total_active_summaries ?? 0;

  const {
    data: interviewer,
    isLoading: interviewerLoading,
  } = useQuery({
    queryKey: ['interviewer', interviewerId],
    queryFn: async () => {
      if (!interviewerId) throw new Error('Interviewer ID required');
      const response = await onboardingService.getInterviewer(interviewerId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch Agent Knowledge');
      }
      return response.data;
    },
    enabled: !!interviewerId,
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      const latestBatch = data?.ingestion_batches?.[0];
      const shouldPollIngestion =
        !!data &&
        (['processing', 'planning'].includes(data.processing_status) ||
          (!!latestBatch && !['completed', 'failed'].includes(latestBatch.status)));
      const shouldPollPromptRefresh =
        !!data && (
          data.prompt_status === 'regenerating'
          || data.config_generation_status === 'generating'
          || generationPending
        );

      if (shouldPollPromptRefresh) return 3000;
      if (shouldPollIngestion) return 5000;
      return false;
    },
  });

  useEffect(() => {
    const promptRefreshActive =
      interviewer?.config_generation_status === 'generating'
      || interviewer?.prompt_status === 'regenerating';
    if (!promptRefreshActive) {
      setGenerationPending(false);
    }
  }, [interviewer?.config_generation_status, interviewer?.prompt_status]);

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
  } = useQuery({
    queryKey: ['interviewer-sessions', interviewerId],
    queryFn: async () => {
      if (!interviewerId) throw new Error('Interviewer ID required');
      const response = await onboardingService.listInterviewerSessions(interviewerId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch interviews');
      }
      return response.data;
    },
    enabled: !!interviewerId,
    retry: false,
  });

  const {
    data: sessionDetail,
    isLoading: sessionDetailLoading,
  } = useQuery({
    queryKey: ['interviewer-session-detail', interviewerId, selectedSessionId],
    queryFn: async () => {
      if (!interviewerId || !selectedSessionId) throw new Error('IDs required');
      const response = await onboardingService.getInterviewerSessionDetail(
        interviewerId,
        selectedSessionId
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch interview detail');
      }
      return response.data;
    },
    enabled: !!interviewerId && !!selectedSessionId,
    retry: false,
  });

  // Sync active tab when the URL changes (e.g. sidebar link while already on /agent-knowledge).
  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleTabChange = (value: string) => {
    if (!(VALID_TABS as readonly string[]).includes(value)) return;
    const nextTab = value as AgentKnowledgeTab;
    setActiveTab(nextTab);
    if (nextTab === 'overview') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: nextTab }, { replace: true });
    }
  };

  const handleRefreshSources = async () => {
    if (!interviewer || !interviewerId) return;
    const result = await reingestInterviewer(interviewer.id, 'full_rebuild');
    if (result) {
      queryClient.invalidateQueries({ queryKey: ['interviewer', interviewerId] });
      queryClient.invalidateQueries({ queryKey: ['interviewers', interviewer.organization_id] });
    }
  };

  const sessions = sessionsData?.sessions || [];

  return (
    <>
      <Helmet>
        <title>Agent Knowledge | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader
          title="Agent Knowledge"
          titleTooltip="Manage what your agent knows, which sources feed it, and which gaps still need attention."
        />

        {/* Tabs + Content */}
        <div className="flex-1 px-4 sm:px-6 md:px-8 py-6">
          {interviewersLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : !primaryInterviewer ? (
            <InterviewerLinksContent />
          ) : interviewerLoading || !interviewer || !interviewerId ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="knowledge">Knowledge</TabsTrigger>
                  <TabsTrigger value="gaps" className="gap-1.5">
                    Gaps
                    {activeGapCount > 0 && (
                      <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
                        {activeGapCount}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="interviews">Interviews</TabsTrigger>
                  <TabsTrigger value="sources">Sources</TabsTrigger>
                </TabsList>

                {activeTab === 'gaps' && (
                  <div className="inline-flex rounded-md bg-muted p-1 text-muted-foreground">
                    {[
                      ['active', 'Open Gaps'],
                      ['analysis', 'Find Gaps'],
                      ['history', 'History'],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setActiveGapsView(value as typeof activeGapsView)}
                        className={cn(
                          'inline-flex h-8 items-center justify-center whitespace-nowrap rounded-sm px-3 text-sm font-medium transition-colors',
                          activeGapsView === value
                            ? 'bg-background text-foreground shadow-sm'
                            : 'hover:text-foreground'
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === 'sources' && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setShowAttachDialog(true)}>
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      Add Sources
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleRefreshSources} disabled={isReingesting}>
                      {isReingesting ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Refresh Sources
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => navigate('/source-library')}>
                      <Archive className="mr-1.5 h-3.5 w-3.5" />
                      Source Library
                    </Button>
                  </div>
                )}

                {activeTab === 'interviews' && primaryInterviewer && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        const link =
                          primaryInterviewer.interview_link ||
                          `${window.location.origin}/interview/${primaryInterviewer.token}`;
                        window.open(link, '_blank');
                      }}
                    >
                      <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                      Start interview
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyLink(primaryInterviewer)}
                    >
                      <Copy className="mr-1.5 h-3.5 w-3.5" />
                      Copy link
                    </Button>
                  </div>
                )}
              </div>

              <TabsContent value="overview">
                <OverviewTab
                  interviewer={interviewer}
                  interviewerId={interviewerId}
                  activeGapCount={activeGapCount}
                  onGenerationStarted={() => setGenerationPending(true)}
                />
              </TabsContent>
              <TabsContent value="knowledge">
                <KnowledgeTab interviewer={interviewer} activeGapCount={activeGapCount} />
              </TabsContent>
              <TabsContent value="gaps">
                <AgentKnowledgeGapsTab orgId={interviewer.organization_id} activeView={activeGapsView} />
              </TabsContent>
              <TabsContent value="interviews">
                <SessionsTab
                  sessions={sessions}
                  isLoading={sessionsLoading}
                  selectedSessionId={selectedSessionId}
                  onSelectSession={setSelectedSessionId}
                  sessionDetail={sessionDetail || null}
                  sessionDetailLoading={sessionDetailLoading}
                  onBackToList={() => setSelectedSessionId(null)}
                  interviewerId={interviewerId}
                  interviewer={interviewer}
                  onCopyLink={() => primaryInterviewer && copyLink(primaryInterviewer)}
                />
              </TabsContent>
              <TabsContent value="sources">
                <SourcesTab
                  interviewer={interviewer}
                  interviewerId={interviewerId}
                  onAddSources={() => setShowAttachDialog(true)}
                />
              </TabsContent>

              <AttachKnowledgeDialog
                interviewer={interviewer}
                open={showAttachDialog}
                onOpenChange={setShowAttachDialog}
              />
            </Tabs>
          )}
        </div>
      </div>
    </>
  );
};

export default TrainingPage;
